import React, { useMemo, useRef, useState } from 'react';
import { motion } from 'motion/react';
import {
  Calendar,
  MapPin,
  Clock,
  Volume2,
  VolumeX,
  Navigation,
  Wine,
  Shirt,
  Sparkles,
  GlassWater,
  Music,
  Utensils,
} from 'lucide-react';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';
import {
  extractCoupleNames,
  parseFlexibleDate,
} from '@/src/infrastructure/lib/weddingDataHelper';
import { safeLinkUrl } from '@/src/modules/renderer/url-safety';
import { UnboxingGate } from '../_shared/UnboxingGate';
import { PointerFoilGlow } from '../_shared/WowEffects';
import { EmeraldFireflies, VelvetCurtainPart } from '../_shared/SignatureEffects';
import {
  pickWeddingStr,
  resolveCeremonySlots,
  resolveCoupleGallery,
  resolveCouplePhotos,
  resolveEveningBeats,
  resolveMenuItems,
  resolveExpMeta,
} from '../_shared/octoberWeddingFields';
import '../_shared/wow-effects.css';
import './theme.css';

const META: TemplateMetadata = {
  id: 'exp-emerald-noir-reception',
  name: 'Emerald Noir Reception',
  occasionType: 'WEDDING',
  description:
    'After-dark cocktail & reception: velvet emerald, gold foil, wax-seal unboxing, multi-event itinerary, RSVP.',
  isPremium: true,
  tags: ['wedding', 'reception', 'cocktail', 'dark', 'emerald', 'luxury', 'premium', 'evening', 'multi-ceremony'],
  colorScheme: { primary: '#0B3D2E', accent: '#D4AF37', bg: '#050807' },
};

const VELVET = '/assets/october-templates/emerald-velvet.jpg';
const HALL = '/assets/october-templates/emerald-cocktail-hall.jpg';

const EVENT_SLOTS = ['Mehendi', 'Sangeet', 'Wedding', 'Reception', 'After-party'] as const;

const BEAT_ICONS = [GlassWater, Sparkles, Utensils, Music, Wine, Calendar] as const;

export function EmeraldNoirReception({ data }: { data: InvitationData }) {
  const [muted, setMuted] = useState(true);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const expMeta = resolveExpMeta(data);
  const wedding = data.customData?.wedding;
  const extracted = extractCoupleNames(data.eventName);

  const groomName = pickWeddingStr(
    wedding?.groom?.fullName,
    wedding?.groom?.nickname,
    expMeta.groomName,
    extracted?.groom,
  );
  const brideName = pickWeddingStr(
    wedding?.bride?.fullName,
    wedding?.bride?.nickname,
    expMeta.brideName,
    extracted?.bride,
  );
  const coupleTitle =
    groomName && brideName ? `${groomName} & ${brideName}` : groomName || brideName || data.eventName || '';

  const parsedDate = parseFlexibleDate(data.eventDate);
  const eventDateLabel = parsedDate
    ? parsedDate.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    : pickWeddingStr(data.eventDate);

  const venue = pickWeddingStr(data.eventVenue, wedding?.weddingEvents?.[0]?.venue);
  const hostName = pickWeddingStr(data.hostName);
  const tagline = pickWeddingStr(
    data.customMessage,
    expMeta.tagline as string,
    'Black tie optional. Join us for an evening under emerald light.',
  );
  const dressCode = pickWeddingStr(expMeta.dressCode as string);

  const { bridePhoto, groomPhoto, couplePhoto } = resolveCouplePhotos(data);
  const gallery = useMemo(() => resolveCoupleGallery(data), [data]);
  const shaadiEvents = useMemo(
    () => resolveCeremonySlots(data, EVENT_SLOTS, venue),
    [data, venue],
  );
  const menuItems = useMemo(() => resolveMenuItems(data), [data]);
  const eveningBeats = useMemo(() => resolveEveningBeats(data), [data]);

  const customVideoUrl = safeLinkUrl(
    pickWeddingStr(
      (data.customData as { customVideoUrl?: string } | null)?.customVideoUrl,
      expMeta.customVideoUrl as string,
    ),
  );
  const rsvpHref = safeLinkUrl(data.rsvpLink);

  const mapsUrl = venue
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venue)}`
    : '';

  const toggleAudio = () => {
    const el = audioRef.current;
    if (!el) {
      setMuted((m) => !m);
      return;
    }
    if (muted) {
      void el.play().catch(() => undefined);
      setMuted(false);
    } else {
      el.pause();
      setMuted(true);
    }
  };

  const body = (
    <VelvetCurtainPart active>
    <div className="enr-root" data-template="exp-emerald-noir-reception">
      <audio
        ref={audioRef}
        loop
        preload="none"
        src="https://cdn.pixabay.com/download/audio/2021/08/04/audio_0625c1539c.mp3?filename=cinematic-ambient-116199.mp3"
      />
      <button type="button" className="enr-audio" onClick={toggleAudio} aria-label={muted ? 'Play audio' : 'Mute audio'}>
        {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
      </button>

      {/* Root-level foil so hover tracks across middle sections, not only hero/RSVP */}
      <PointerFoilGlow className="enr-foil-global" />

      <section className="enr-hero">
        <div className="enr-hero-bg" style={{ backgroundImage: `url(${HALL})` }} aria-hidden />
        {couplePhoto ? (
          <div
            className="enr-hero-couple"
            style={{ backgroundImage: `url(${couplePhoto})` }}
            aria-hidden
          />
        ) : null}
        <div className="enr-hero-veil" aria-hidden />
        {/* Emerald signature: fireflies + curtain part (NOT bells/sand/rangoli) */}
        <EmeraldFireflies count={22} />
        <div className="enr-foil-corners" aria-hidden>
          <span />
          <span />
          <span />
          <span />
        </div>

        <p className="enr-eyebrow">
          <Wine size={13} /> Cocktail reception
        </p>
        <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="enr-names">
          {coupleTitle || 'Reception'}
        </motion.h1>
        <p className="enr-event">{data.eventName || 'An evening celebration'}</p>

        <div className="enr-glass enr-meta">
          {eventDateLabel ? (
            <span>
              <Calendar size={14} /> {eventDateLabel}
            </span>
          ) : null}
          {data.eventTime ? (
            <span>
              <Clock size={14} /> {data.eventTime}
            </span>
          ) : null}
          {venue ? (
            <span>
              <MapPin size={14} /> {venue}
            </span>
          ) : null}
        </div>
        {hostName ? <p className="enr-host">Hosted by {hostName}</p> : null}
      </section>

      {/* Zareqia-style venue spotlight — full-bleed hall with glass overlay */}
      <section className="enr-spotlight">
        <div className="enr-spotlight-bg" style={{ backgroundImage: `url(${HALL})` }} aria-hidden />
        <div className="enr-spotlight-veil" aria-hidden />
        <EmeraldFireflies count={10} />
        <div className="enr-glass enr-spotlight-card">
          <p className="enr-spotlight-kicker">Tonight&apos;s setting</p>
          <h2>{venue || 'The emerald room'}</h2>
          <p>
            Candlelight, crystal, and velvet light across the room. Arrive ready to celebrate under
            emerald and gold.
          </p>
        </div>
      </section>

      <section className="enr-photos">
        <figure className={groomPhoto ? undefined : 'enr-photo--empty'}>
          {groomPhoto ? (
            <img src={groomPhoto} alt={groomName || 'Groom'} />
          ) : (
            <span>Groom photo</span>
          )}
          <figcaption>{groomName || 'Groom'}</figcaption>
        </figure>
        <figure className={bridePhoto ? undefined : 'enr-photo--empty'}>
          {bridePhoto ? (
            <img src={bridePhoto} alt={brideName || 'Bride'} />
          ) : (
            <span>Bride photo</span>
          )}
          <figcaption>{brideName || 'Bride'}</figcaption>
        </figure>
      </section>

      {gallery.length > 0 ? (
        <section className="enr-gallery" aria-label="Couple photo gallery">
          <h2>Gallery</h2>
          <div className="enr-gallery-grid">
            {gallery.map((item) => (
              <figure key={item.key}>
                {item.isVideo ? (
                  <video src={item.url} controls playsInline preload="metadata" />
                ) : (
                  <img src={item.url} alt={item.caption || 'Memory'} loading="lazy" />
                )}
                {item.caption ? <figcaption>{item.caption}</figcaption> : null}
              </figure>
            ))}
          </div>
        </section>
      ) : null}

      <section className="enr-glass enr-copy">
        <div className="enr-gem" aria-hidden />
        <p>{tagline}</p>
      </section>

      <section className="enr-beats">
        <h2>
          <Sparkles size={16} /> Evening flow
        </h2>
        <ol>
          {eveningBeats.map((b, i) => {
            const Icon = BEAT_ICONS[i % BEAT_ICONS.length];
            return (
              <li key={`${b.label}-${i}`} className={b.filled ? undefined : 'enr-beat--placeholder'}>
                <span className="enr-beat-icon" aria-hidden>
                  <Icon size={16} />
                </span>
                <strong>{b.t}</strong>
                <span>{b.label}</span>
              </li>
            );
          })}
        </ol>
      </section>

      <section className="enr-events" aria-label="Wedding events">
        <h2>
          <Calendar size={16} /> The celebrations
        </h2>
        <ul className="enr-events-grid">
          {shaadiEvents.map((ev) => (
            <li
              key={ev.key}
              className={`enr-glass enr-event-card${ev.filled ? '' : ' enr-event-card--placeholder'}`}
            >
              {ev.coverImageUrl ? (
                <img className="enr-event-cover" src={ev.coverImageUrl} alt="" loading="lazy" />
              ) : null}
              <h3>{ev.label}</h3>
              {ev.filled ? (
                <>
                  {ev.date ? <p>{ev.date}</p> : null}
                  {ev.time ? <p>{ev.time}</p> : null}
                  {ev.venue ? <p className="enr-event-venue">{ev.venue}</p> : null}
                </>
              ) : null}
            </li>
          ))}
        </ul>
      </section>

      {menuItems.some((item) => item.filled) ? (
      <section className="enr-menu" aria-label="Evening menu highlights">
        <h2>On the menu</h2>
        <div className="enr-menu-grid">
          {menuItems.map((item) => (
            <div
              key={item.title}
              className={`enr-glass enr-menu-card${item.filled ? '' : ' enr-menu-card--placeholder'}`}
            >
              <h3>{item.title}</h3>
              <p>{item.note}</p>
            </div>
          ))}
        </div>
      </section>
      ) : null}

      {dressCode ? (
        <section className="enr-glass enr-dress">
          <Shirt size={18} />
          <div>
            <h2>Dress code</h2>
            <p>{dressCode}</p>
          </div>
        </section>
      ) : null}

      {customVideoUrl ? (
        <section className="enr-video">
          <h2>Memories</h2>
          <video controls playsInline src={customVideoUrl} />
        </section>
      ) : null}

      {mapsUrl ? (
        <section className="enr-map">
          <a href={mapsUrl} target="_blank" rel="noreferrer">
            <Navigation size={15} /> Directions to the venue
          </a>
        </section>
      ) : null}

      <section id="enr-rsvp" className="enr-glass enr-rsvp">
        <h2>RSVP</h2>
        <p>We cannot wait to celebrate with you under the emerald lights.</p>
        {rsvpHref ? (
          <a className="enr-cta" href={rsvpHref}>
            Open RSVP
          </a>
        ) : (
          <p className="enr-hint">RSVP unlocks after publish.</p>
        )}
      </section>
    </div>
    </VelvetCurtainPart>
  );

  return (
    <UnboxingGate
      title={coupleTitle || 'Reception'}
      accent="#D9B65A"
      background="#041510"
      textureUrl={VELVET}
      sealLabel="Evening reception"
    >
      {body}
    </UnboxingGate>
  );
}

registry.register({ metadata: META, component: EmeraldNoirReception });
